import type { ChartConfiguration } from "chart.js";

export type OrganizationSeries = {
  label: string; // org abbreviation, e.g. "CWS"
  data: number[]; // one count per bucket, aligned with `buckets`
};

// Stacked bars: one bar per time bucket (e.g. per year), stacked by org.
export type OrganizationBarData = {
  kind: "bar";
  buckets: string[]; // e.g. ["2024", "2025"]
  series: OrganizationSeries[];
};

// A single all-time pie, the long tail folded into an "Other" slice server-side.
export type OrganizationPieData = {
  kind: "pie";
  labels: string[]; // e.g. ["CWS", "ABC", "Other"]
  values: number[]; // aligned with `labels`
};

export type OrganizationRequestsData = OrganizationBarData | OrganizationPieData;

// A repeating palette of distinct, legible colors. Cycled when there are more
// organizations than colors — collisions only happen past PALETTE.length orgs.
const PALETTE = [
  "#7B9669", // jade-500
  "#404E3B", // forest-900
  "#6C8480", // sage-500
  "#BAC8B1", // moss-200
  "#C7A27C", // clay
  "#8E7CC3", // iris
  "#D98C8C", // rose
  "#5B8AA6", // steel-blue
];

export function buildChartConfig(data: OrganizationRequestsData): ChartConfiguration {
  return data.kind === "pie" ? buildPieConfig(data) : buildBarConfig(data);
}

function buildPieConfig(data: OrganizationPieData): ChartConfiguration {
  return {
    type: "pie",
    data: {
      labels: data.labels,
      datasets: [
        {
          data: data.values,
          backgroundColor: data.labels.map((_, index) => PALETTE[index % PALETTE.length]),
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "right" } },
    },
  };
}

function buildBarConfig(data: OrganizationBarData): ChartConfiguration {
  const datasets = data.series.map((series, index) => ({
    type: "bar" as const,
    label: series.label,
    data: series.data,
    backgroundColor: PALETTE[index % PALETTE.length],
    stack: "organizations",
  }));

  return {
    type: "bar",
    data: {
      labels: data.buckets,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      scales: {
        x: { stacked: true },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: { precision: 0 },
          title: { display: true, text: "Ride requests" },
        },
      },
    },
  };
}
